"use client";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useMediaQuery } from "@/lib/useClientValue";

// Köşe toast'ının ortak gövdesi (F6): giriş/çıkış geçişi, geri sayım çubuğu,
// üzerine gelince duran sayaç, kapat düğmesi, isteğe bağlı eylem düğmesi.
// İki watcher (rapor, hasta üretimi) yalnızca içerik ve konum verir; beat
// değişince (`beatKey`) kart yeniden tanıtılır, sessizce yeniden yazılmaz.
//
// Beat başına kendini kapatma süresi: `autoDismissMs` null ise sayaç yok, kart
// öğrenciyi bekler ("failed" gibi tek tutamak beat'ler). Süre dolunca `onClose`.

const TICK_MS = 100;
// Enter/leave transition. Short on purpose: the toast has to feel like it settled
// into place, not like it is putting on a show while the student is reading.
const ANIM_MS = 180;

export function Toast({
  position, beatKey, tone, icon, title, body, cta, onCta, autoDismissMs, onClose, dismissLabel, autoCloseLabel,
}: {
  position: "top" | "bottom";
  /** Beat kimliği: değişince giriş geçişi ve sayaç yeniden başlar. */
  beatKey: string;
  tone: "accent" | "warn";
  icon: ReactNode;
  title: string;
  body: string;
  cta?: string | null;
  onCta?: () => void;
  autoDismissMs: number | null;
  onClose: () => void;
  dismissLabel: string;
  autoCloseLabel: (seconds: number) => string;
}) {
  // Milliseconds left before this beat closes itself (drives the bottom bar).
  const [remaining, setRemaining] = useState<number | null>(null);
  // Transition state: `shown` flips on one frame after mount (so the browser has a
  // "from" style to animate out of), `leaving` holds the card on screen for the
  // exit transition before the beat is actually torn down.
  const [shown, setShown] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // OS "daha az hareket" tercihi render sırasında okunur (F19). A student who
  // asked the OS for less motion gets the toast with no movement at all.
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const pausedRef = useRef(false);
  const closeRef = useRef<(() => void) | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Shared by the close button and the countdown, so both behave identically: play
  // the exit transition first, tear the beat down only once it has finished.
  const close = useCallback(() => {
    if (reduceMotion) { onClose(); return; }
    if (leaveTimer.current) return; // already on its way out
    setLeaving(true);
    leaveTimer.current = setTimeout(() => {
      leaveTimer.current = null;
      setLeaving(false);
      onClose();
    }, ANIM_MS);
  }, [onClose, reduceMotion]);
  useEffect(() => () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); }, []);
  // Held in a ref so the countdown interval always calls the current version
  // without having to restart every time `close` is re-created.
  useEffect(() => { closeRef.current = close; }, [close]);

  // Enter transition, re-run per beat: a beat change (running → ready) swaps the
  // content of the same card, so it is re-introduced rather than silently rewritten.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- CSS giriş geçişi iki aşamalı commit ister (önce kapalı, bir kare sonra açık); bu bir animasyon durum makinesidir, türetilemez.
    setShown(false);
    // A beat that arrives while the previous one is fading out cancels that exit,
    // otherwise the pending timer would tear down the beat that just replaced it.
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    setLeaving(false);
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [beatKey]);

  // Countdown to the automatic close. Restarts whenever a new beat arrives, and
  // holds while the pointer or keyboard focus is on the toast so nothing vanishes
  // mid-read (or mid-click).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- geri sayım zamanlayıcısı: kalan süre bir aralıkla düşer, başlangıcı beat değişince sıfırlanır.
    if (autoDismissMs === null) { setRemaining(null); return; } // this beat waits for the user
    setRemaining(autoDismissMs);
    let left = autoDismissMs;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      left -= TICK_MS;
      if (left <= 0) {
        clearInterval(id);
        setRemaining(0);
        closeRef.current?.();
        return;
      }
      setRemaining(left);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [beatKey, autoDismissMs]);

  const iconStyle = tone === "warn"
    ? { background: "var(--accent-warm-pale)", color: "var(--accent-warm)" }
    : { background: "var(--accent-light)", color: "var(--accent)" };

  // On screen and staying there: mounted, past the first frame, not on its way out.
  const settled = shown && !leaving;
  const total = autoDismissMs;
  const left = total === null ? 0 : remaining ?? total;
  // Seconds shown to the user: round UP so a fresh toast reads "8 sn", not "7 sn",
  // and the number never sits on 0 while the card is still there.
  const secondsLeft = Math.max(1, Math.ceil(left / 1000));

  return (
    <div
      role="status"
      aria-live="polite"
      // overflow-hidden + no padding on the card itself: the countdown bar runs
      // edge to edge along the bottom, inside the rounded corners.
      className={`fixed ${position === "top" ? "top-5" : "bottom-5"} right-5 z-50 w-[min(22rem,calc(100vw-2.5rem))] overflow-hidden rounded-2xl border shadow-lg`}
      style={{
        background: "var(--surface)",
        borderColor: "var(--border)",
        // `settled` is the resting state; both the pre-enter and the leaving state
        // are the same faded, slightly smaller card, so it grows in and shrinks out.
        ...(reduceMotion
          ? {}
          : {
              opacity: settled ? 1 : 0,
              transform: settled ? "scale(1)" : "scale(0.96)",
              transformOrigin: position === "top" ? "top right" : "bottom right",
              transition: `opacity ${ANIM_MS}ms ease-out, transform ${ANIM_MS}ms ease-out`,
            }),
      }}
      // The countdown holds while the toast is being read or reached for.
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onFocusCapture={() => { pausedRef.current = true; }}
      onBlurCapture={() => { pausedRef.current = false; }}
    >
      <div className="flex items-start gap-3 p-4">
        <span
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          style={iconStyle}
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
          <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{body}</p>
          {cta && (
            <button
              onClick={onCta}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold text-white hover:opacity-90"
              style={{ background: "var(--accent)" }}
            >
              {cta}
              <svg aria-hidden className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <button
            onClick={close}
            aria-label={dismissLabel}
            className="rounded-full p-1 text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            <svg aria-hidden className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
          {/* The number spells out what the bar below only implies. */}
          {total !== null && (
            <span className="text-[10px] font-medium tabular-nums text-[var(--text-muted)]">
              {autoCloseLabel(secondsLeft)}
            </span>
          )}
        </div>
      </div>

      {/* Countdown bar, flush with the card's bottom edge. Absent on the beats
          that never close by themselves. */}
      {total !== null && (
        <div className="h-[3px] w-full" style={{ background: "var(--border)" }}>
          <div
            className="h-full"
            style={{
              width: `${Math.max(0, (left / total) * 100)}%`,
              background: "var(--accent)",
              // Matches the tick so the bar glides instead of stepping; `linear`
              // keeps the drain rate honest.
              transition: `width ${TICK_MS}ms linear`,
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Spinning ring: the one beat that is still in motion. */
export function SpinnerIcon() {
  return (
    <span
      aria-hidden
      className="h-4 w-4 animate-spin rounded-full border-2 border-current"
      style={{ borderTopColor: "transparent" }}
    />
  );
}

export function WarningIcon() {
  return (
    <svg aria-hidden className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
  );
}
